/**
 * Vendor-account-suspended top-of-shell banner.
 *
 * Rendered by the apps/sell SellShell at the top of every page when the
 * caller's active vendor has `status = 'suspended'`. Persistent (not
 * dismissible) and high-severity -- the operator must SEE this every
 * time they load the app, because every editable surface is greyed
 * out and they need to understand why.
 *
 * The "Contact admins" link points at the Help inbox shipped earlier
 * (`/help` on apps/sell). The Help thread page intentionally stays
 * outside the lockdown so suspended vendors retain an appeal channel.
 *
 * The styling mirrors `OrderAdminBanner`'s `warning` tone, which is the
 * heaviest tier in the existing palette: red tint on linen, same border
 * weight, same icon glyph -- so this banner reads as the same severity
 * tier the operator already recognises from admin warnings on order
 * pages.
 */

import { useTranslation } from 'react-i18next';
import { cx } from './cx';

export interface VendorSuspendedBannerProps {
  /**
   * Suspension reason captured at the suspend RPC call site
   * (`shop.rpc_admin_suspend_vendor`'s `p_reason`). When null we fall
   * back to a generic "contact support for details" hint.
   */
  reason: string | null;
  /**
   * Where the "Contact admins" link points. apps/sell wires this to
   * `/help` (the in-app help inbox); other apps pass their own path.
   */
  helpHref?: string;
  className?: string;
}

export function VendorSuspendedBanner({
  reason,
  helpHref = '/help',
  className,
}: VendorSuspendedBannerProps) {
  const { t } = useTranslation();
  const reasonText =
    reason && reason.trim().length > 0
      ? reason.trim()
      : t('shopUi.suspended.bannerContactSupport');
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cx(
        'border bg-[#A8451E29] text-[#A8451E] border-[#A8451E66]',
        'px-4 py-3 flex items-start gap-3 text-sm',
        className,
      )}
      data-testid="vendor-suspended-banner"
    >
      <span aria-hidden className="text-base leading-none mt-0.5">
        {'\u{1F6A8}'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium uppercase tracking-wide text-xs opacity-80 mb-0.5">
          {t('shopUi.suspended.bannerTitle')}
        </div>
        <div className="whitespace-pre-wrap break-words leading-snug">
          <strong>{t('shopUi.suspended.bannerBody')}</strong>{' '}
          {t('shopUi.suspended.reason')} {reasonText}
        </div>
        <div className="mt-1">
          <a
            href={helpHref}
            className="underline underline-offset-2 font-medium hover:opacity-80"
          >
            {t('shopUi.suspended.bannerContactAdmins')}
          </a>
        </div>
      </div>
    </div>
  );
}
